import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Textarea,
  Alert,
  IconButton,
  Stack
} from '@mui/joy';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Switch
} from '@mui/joy';
import { Add, Edit, Delete, CalendarMonth } from '@mui/icons-material';
import { PreferenceService } from '@/services/preferenceService';
import { useAuth } from '@/context/AuthContext';
import {
  DatePreference,
  CreateDatePreferenceData,
  UpdateDatePreferenceData,
  DATE_PREFERENCE_TYPES,
  PRIORITY_OPTIONS
} from '@/types/preference';

interface DatePreferencesTabProps {
  preferences: DatePreference[];
  onUpdate: () => void;
}

const DatePreferencesTab: React.FC<DatePreferencesTabProps> = ({
  preferences,
  onUpdate
}) => {
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPreference, setEditingPreference] = useState<DatePreference | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateDatePreferenceData>({
    employee_id: user?.id || 0,
    preference_date: '',
    preference_type: 'unavailable',
    is_recurring: false,
    recurrence_pattern: '',
    recurrence_interval: 1,
    reason: '',
    priority: 'medium'
  });

  const handleOpenDialog = (preference?: DatePreference) => {
    if (preference) {
      setEditingPreference(preference);
      setFormData({
        employee_id: preference.employee_id,
        preference_date: preference.preference_date,
        preference_type: preference.preference_type,
        is_recurring: preference.is_recurring,
        recurrence_pattern: preference.recurrence_pattern || '',
        recurrence_interval: preference.recurrence_interval || 1,
        reason: preference.reason || '',
        priority: preference.priority
      });
    } else {
      setEditingPreference(null);
      setFormData({
        employee_id: user?.id || 0,
        preference_date: '',
        preference_type: 'unavailable',
        is_recurring: false,
        recurrence_pattern: '',
        recurrence_interval: 1,
        reason: '',
        priority: 'medium'
      });
    }
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPreference(null);
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const errors = PreferenceService.validateDatePreference(formData);
      if (errors.length > 0) {
        setError(errors.join(', '));
        return;
      }

      if (editingPreference) {
        await PreferenceService.updateDatePreference(editingPreference.id, formData);
      } else {
        await PreferenceService.createDatePreference(formData);
      }

      handleCloseDialog();
      onUpdate();
    } catch (err) {
      console.error('Error saving date preference:', err);
      setError('Failed to save preference. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (preference: DatePreference) => {
    if (window.confirm('Are you sure you want to delete this preference?')) {
      try {
        await PreferenceService.deleteDatePreference(preference.id);
        onUpdate();
      } catch (err) {
        console.error('Error deleting preference:', err);
        setError('Failed to delete preference. Please try again.');
      }
    }
  };

  const getPreferenceTypeColor = (type: string) => {
    return type === 'unavailable' ? 'danger' : 'success';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'neutral';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography level="h4">
          Date Preferences
        </Typography>
        <Button
          startDecorator={<Add />}
          onClick={() => handleOpenDialog()}
          variant="solid"
        >
          Add Date Preference
        </Button>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {preferences.length === 0 ? (
        <Card variant="soft">
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CalendarMonth sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
              <Typography level="title-sm" color="neutral">
                No date preferences set
              </Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Add date preferences to mark specific dates as unavailable or preferred.
              </Typography>
              <Button onClick={() => handleOpenDialog()}>
                Add Your First Date Preference
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {preferences.map((preference) => (
            <Grid xs={12} md={6} lg={4} key={preference.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography level="title-sm">
                        {new Date(preference.preference_date).toLocaleDateString()}
                      </Typography>
                      <Typography level="body-sm" color="neutral">
                        {preference.is_recurring ? 'Recurring' : 'One-time'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        onClick={() => handleOpenDialog(preference)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleDelete(preference)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip
                      size="sm"
                      color={getPreferenceTypeColor(preference.preference_type)}
                    >
                      {preference.preference_type}
                    </Chip>
                    <Chip
                      size="sm"
                      color={getPriorityColor(preference.priority)}
                      variant="soft"
                    >
                      {preference.priority}
                    </Chip>
                  </Stack>

                  {preference.reason && (
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      <strong>Reason:</strong> {preference.reason}
                    </Typography>
                  )}

                  {preference.is_recurring && preference.recurrence_pattern && (
                    <Typography level="body-sm" color="neutral">
                      Repeats: {preference.recurrence_pattern}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Modal */}
      <Modal open={openDialog} onClose={handleCloseDialog}>
        <ModalDialog size="sm">
          <ModalClose />
          <Typography level="title-sm" component="h2">
            {editingPreference ? 'Edit Date Preference' : 'Add Date Preference'}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl>
              <FormLabel>Date</FormLabel>
              <Input
                type="date"
                value={formData.preference_date}
                onChange={(e) => setFormData({ ...formData, preference_date: e.target.value })}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Preference Type</FormLabel>
              <Select
                value={formData.preference_type}
                onChange={(e, value) => setFormData({ ...formData, preference_type: value || 'unavailable' })}
              >
                {DATE_PREFERENCE_TYPES.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Priority</FormLabel>
              <Select
                value={formData.priority}
                onChange={(e, value) => setFormData({ ...formData, priority: value || 'medium' })}
              >
                {PRIORITY_OPTIONS.map((priority) => (
                  <Option key={priority.value} value={priority.value}>
                    {priority.label}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={!!formData.is_recurring}
                onChange={(event) => {
                  const checked = event.target?.checked ?? false;
                  setFormData((prev) => ({ ...prev, is_recurring: checked }));
                }}
              />
              <Typography level="body-sm">Recurring preference</Typography>
            </Box>

            {formData.is_recurring && (
              <>
                <FormControl>
                  <FormLabel>Recurrence Pattern</FormLabel>
                  <Select
                    value={formData.recurrence_pattern}
                    onChange={(e, value) => setFormData({ ...formData, recurrence_pattern: value || '' })}
                  >
                    <Option value="weekly">Weekly</Option>
                    <Option value="monthly">Monthly</Option>
                    <Option value="yearly">Yearly</Option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Recurrence Interval</FormLabel>
                  <Input
                    type="number"
                    value={formData.recurrence_interval}
                    onChange={(e) => setFormData({ ...formData, recurrence_interval: parseInt(e.target.value) || 1 })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>End Date (Optional)</FormLabel>
                  <Input
                    type="date"
                    value={formData.recurrence_end_date || ''}
                    onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                  />
                </FormControl>
              </>
            )}

            <FormControl>
              <FormLabel>Reason (Optional)</FormLabel>
              <Textarea
                minRows={2}
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Explain why you have this preference..."
              />
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2 }}>
            <Button variant="plain" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={loading}
              disabled={loading}
            >
              {editingPreference ? 'Update' : 'Create'}
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default DatePreferencesTab; 